import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { selectUserRoles } from '../../features/auth/authSlice';
import { useGetAdminInvestorQuery, useAdminAttachInvestorDocumentMutation } from '../../features/investors/adminInvestorsApi';
import { useListUserInvestmentsQuery } from '../../features/investments/adminInvestmentsApi';
import { usePlanLookup } from '../../components/investments/usePlanLookup';
import InvestmentTable from '../../components/investments/InvestmentTable';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { DataRow } from '../../components/ui/MobileCard';
import { SkeletonDetailCard, SkeletonTable } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';
import { KYC_OTHER_DOCUMENT_TAGS, kycOtherDocumentTagSchema, kycFileSchema } from '../../lib/validators/kycSchema';
import KycTagSelect from '../../components/kyc/KycTagSelect';
import KycProgressBar from '../../components/kyc/KycProgressBar';
import { UploadIcon, DownloadIcon } from '../../components/ui/icons';

const attachDocumentSchema = z.object({
  tag: kycOtherDocumentTagSchema,
  file: kycFileSchema,
});

const STATUS_TONE = { pending_approval: 'warning', active: 'success', rejected: 'danger', suspended: 'neutral' };

// v1.17.0: same merged status/pending-payout dropdown as the two other
// investment list screens (admin global list, investor's own list) —
// 'pending_payout' is a client-only sentinel, mutually exclusive with a
// real status param, never sent to the server as-is.
const INVESTMENT_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending_funding', label: 'Pending funding' },
  { value: 'active', label: 'Active' },
  { value: 'matured', label: 'Matured' },
  { value: 'closed', label: 'Closed' },
  { value: 'pending_payout', label: 'Pending payout' },
];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * GET /admin/investors/{uuid} — 404 for a uuid that doesn't exist OR
 * belongs to a non-investor (enumeration-safe, same pattern as admin
 * accounts in reverse) — authErrorMessage's notFound override covers both
 * with one message rather than trying to tell them apart.
 *
 * "Create investment" is gated on manage-investments (independent from the
 * manage-investors permission that gates this whole screen) AND on
 * account_status === 'active' — the server 409s otherwise, but disabling
 * the button client-side avoids a pointless round trip and explains why
 * up front.
 */
export default function InvestorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const roles = useSelector(selectUserRoles);
  const canManageInvestments = roles.includes('admin') || roles.includes('super-admin');

  const [attachInvestorDocument, { isLoading: isAttaching }] = useAdminAttachInvestorDocumentMutation();
  const [attachOpen, setAttachOpen] = useState(false);
  const attachForm = useForm({ resolver: zodResolver(attachDocumentSchema), defaultValues: { tag: '', file: undefined } });

  const closeAttach = () => {
    setAttachOpen(false);
    attachForm.reset({ tag: '', file: undefined });
  };

  const onAttachDocument = async (values) => {
    try {
      const formData = new FormData();
      formData.append('tag', values.tag);
      formData.append('file', values.file);
      await attachInvestorDocument({ uuid: id, formData }).unwrap();
      toast.push({ tone: 'success', message: 'Document attached.' });
      closeAttach();
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => attachForm.setError(field, { message: messages[0] }));
        return;
      }
      attachForm.setError('root', { message: authErrorMessage(err, { fallback: 'Could not attach this document.' }) });
    }
  };

  const [status, setStatus] = useState('');
  const isPendingPayoutFilter = status === 'pending_payout';
  const { data: investor, isLoading, isError, error } = useGetAdminInvestorQuery(id, { skip: !id });
  const { data: investmentsData, isLoading: isLoadingInvestments } = useListUserInvestmentsQuery(
    {
      uuid: id,
      status: isPendingPayoutFilter ? undefined : status || undefined,
      has_pending_payout: isPendingPayoutFilter,
    },
    { skip: !id }
  );
  const getPlan = usePlanLookup();

  const investments = investmentsData?.investments ?? [];
  const isActive = investor?.account_status === 'active';

  const goToCreateInvestment = () => {
    navigate('/admin/investments/new', { state: { userUuid: investor.id, userName: investor.full_name } });
  };

  return (
    <div className="space-y-6">
      <Link to="/admin/investors" className="text-sm text-accent hover:underline">
        ← Back to investors
      </Link>

      {isLoading && <SkeletonDetailCard fields={4} />}

      {isError && (
        <Card>
          <p className="py-6 text-center text-danger">{authErrorMessage(error, { notFound: 'Investor not found.' })}</p>
        </Card>
      )}

      {investor && (
        <>
          <Card className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-lg font-semibold text-foreground">{investor.full_name}</h1>
                <p className="text-sm text-foreground-muted">{investor.email}</p>
              </div>
              <Badge tone={STATUS_TONE[investor.account_status] || 'neutral'}>
                {(investor.account_status || 'unknown').replace('_', ' ').toUpperCase()}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <DataRow label="Phone" value={investor.phone || '—'} />
              <DataRow label="Last Login" value={investor.last_login_at ? formatDate(investor.last_login_at) : 'Never logged in'} />
              {investor.suspended_at && <DataRow label="Suspended" value={formatDate(investor.suspended_at)} />}
              <DataRow label="Date Joined" value={formatDate(investor.created_at)} />
            </div>

            {canManageInvestments && (
              <div className="pt-3 border-t border-border">
                <Button
                  onClick={goToCreateInvestment}
                  disabled={!isActive}
                  title={!isActive ? 'Investor must be KYC-approved (active) before an investment can be created.' : undefined}
                >
                  Create investment
                </Button>
                {!isActive && (
                  <p className="mt-1.5 text-xs text-foreground-subtle">
                    Investor must be KYC-approved before an investment can be created.
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* v1.18.0 — kyc_progress + documents from GET /admin/investors/{uuid}.
              "Attach Document" is gated by manage-investors (same role check
              as the rest of this screen), NOT manage-kyc-documents — this is
              an investor-record action, independent of the Template
              Management permission. */}
          <Card className="space-y-4">
            <CardHeader
              title="KYC & documents"
              action={
                canManageInvestments && (
                  <Button size="sm" onClick={() => setAttachOpen(true)}>
                    <UploadIcon className="h-3.5 w-3.5" />
                    Attach document
                  </Button>
                )
              }
            />

            {investor.kyc_progress && <KycProgressBar percent={investor.kyc_progress.percent} />}

            <div>
              <p className="text-xs font-medium text-foreground-muted mb-2">Documents</p>
              {(!investor.documents || investor.documents.length === 0) && (
                <p className="text-sm text-foreground-muted">No documents on file.</p>
              )}
              {investor.documents && investor.documents.length > 0 && (
                <div className="space-y-2">
                  {investor.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background-elevated px-3 py-2 text-sm"
                    >
                      <div className="min-w-0 flex items-center gap-2">
                        {doc.tag && <Badge tone="info">{doc.tag}</Badge>}
                        <span className="truncate text-foreground">{doc.original_name}</span>
                      </div>
                      {doc.download_url && (
                        <a
                          href={doc.download_url}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 inline-flex items-center gap-1 text-xs text-accent hover:underline"
                        >
                          <DownloadIcon className="h-3.5 w-3.5" />
                          View
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Investments"
              subtitle="This investor's investments."
              action={
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="rounded-lg bg-background-elevated border border-border px-3 py-1.5 text-xs text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
                >
                  {INVESTMENT_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              }
            />
            {isLoadingInvestments && <SkeletonTable columns={['Plan', 'Principal', 'Funded / Remaining', 'Status', 'Created']} rows={3} />}
            {!isLoadingInvestments && investments.length === 0 && (
              <p className="py-6 text-center text-foreground-muted">No investments yet.</p>
            )}
            {!isLoadingInvestments && investments.length > 0 && (
              <InvestmentTable
                investments={investments}
                getPlan={getPlan}
                onRowClick={(inv) => navigate(`/admin/investments/${inv.id}`)}
              />
            )}
          </Card>
        </>
      )}

      <Modal open={attachOpen} onClose={closeAttach} title="Attach document">
        <form onSubmit={attachForm.handleSubmit(onAttachDocument)} className="space-y-4" noValidate>
          <p className="text-sm text-foreground-muted">
            Attaches a document to this investor on their behalf — the same "Other Documents" checklist they see themselves.
          </p>
          <Controller
            name="tag"
            control={attachForm.control}
            render={({ field }) => (
              <KycTagSelect
                value={field.value}
                onChange={field.onChange}
                knownTags={KYC_OTHER_DOCUMENT_TAGS}
                error={attachForm.formState.errors.tag?.message}
              />
            )}
          />
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">File</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf"
              onChange={(e) => attachForm.setValue('file', e.target.files?.[0], { shouldValidate: true })}
              className="w-full text-sm text-foreground-muted file:mr-3 file:rounded-lg file:border-0 file:bg-background-elevated file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground file:cursor-pointer hover:file:bg-background-surface"
            />
            {attachForm.formState.errors.file && <p className="mt-1.5 text-xs text-danger">{attachForm.formState.errors.file.message}</p>}
          </div>
          {attachForm.formState.errors.root && <p className="text-sm text-danger">{attachForm.formState.errors.root.message}</p>}
          <Button type="submit" className="w-full" isLoading={isAttaching}>
            Attach document
          </Button>
        </form>
      </Modal>
    </div>
  );
}
